import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { useScopedSelect } from '@/contexts/ClientScopeContext';

/** ScreenConnect (RMM → Remote access) — mirrors server/rmm/screenconnect.ts. Read-only. */

export interface ScSession {
  id: string;
  name: string;
  code: string | null;
  site: string | null;
  companyId: number | null;
  companyName: string | null;
  companySource: 'manual' | 'device' | 'none';
  online: boolean;
  hostConnected: boolean;
  loggedOnUser: string | null;
  os: string | null;
  clientVersion: string | null;
  lastConnected: string | null;
  lastJoin: { host: string; at: string | null } | null;
  backstage: boolean;
  hardware: { serial: string | null; mac: string | null; manufacturer: string | null; model: string | null; privateIp: string | null; domain: string | null; fetchedAt: string } | null;
  rmm: { uid: string; hostname: string; online: boolean; matchedBy: 'serial' | 'mac' | 'hostname' } | null;
  /** Dead agent: no Datto device and no connection within the stale window (the `remote-orphan` gap). */
  orphan: boolean;
  /** Host page with the session selected, no join. */
  hostUrl: string;
  joinUrl: string;
  backstageUrl: string;
}

export interface ScConnection { id: string; technician: string; start: string | null; end: string | null; minutes: number | null }

export type ScResponse =
  | { configured: false }
  | { configured: true; sessions: ScSession[]; total: number; fetchedAt: string; details: { read: number; total: number } };

/** Every access session. `scoped`: narrow to the header client picker. */
export function useScreenConnect(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<ScResponse>(opts.scoped, (d, inScope) => d.configured ? { ...d, sessions: d.sessions.filter((x) => inScope(x.companyId)) } : d);
  return useQuery({ select, queryKey: ['screenconnect'], queryFn: () => api.get<ScResponse>('/api/rmm/screenconnect'), staleTime: 60_000, refetchInterval: 5 * 60_000 });
}

export type RemoteState = 'yes' | 'missing' | 'n/a' | 'unknown';
export interface RemoteDeviceCoverage { state: RemoteState; sessionId: string | null; online: boolean | null }
export type RemoteCoverageResponse =
  | { configured: false }
  | { configured: true; ready: boolean; details: { read: number; total: number }; items: Record<string, RemoteDeviceCoverage> };

/** Per Datto device uid: does it have a ScreenConnect session (missing = a gap; unknown = details still loading). */
export function useRemoteCoverage() {
  return useQuery({
    queryKey: ['screenconnect', 'coverage'],
    queryFn: () => api.get<RemoteCoverageResponse>('/api/rmm/screenconnect/coverage'),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Sessions matched to a Datto RMM device (device page / Device Orbit). */
export function useScreenConnectDevice(uid: string | null | undefined) {
  return useQuery({
    enabled: !!uid,
    queryKey: ['screenconnect', 'device', uid],
    queryFn: () => api.get<{ configured: boolean; sessions: ScSession[] }>(`/api/rmm/screenconnect/devices/${uid}`),
    staleTime: 60_000,
  });
}

export interface CiRemote {
  ci: { id: number; name: string | null; companyId: number | null } | null;
  /** The CI's Datto RMM device, whether or not ScreenConnect knows it. */
  rmm: { uid: string; hostname: string; online: boolean; portalUrl: string } | null;
  /** ScreenConnect is configured on the server. */
  screenconnect: boolean;
  sessions: ScSession[];
}

/** A ticket's configuration item → its Datto device + ScreenConnect session (ticket header buttons + side card). */
export function useScreenConnectCi(ciId: number | null | undefined) {
  return useQuery({
    enabled: !!ciId,
    queryKey: ['screenconnect', 'ci', ciId],
    queryFn: () => api.get<CiRemote>(`/api/rmm/screenconnect/ci/${ciId}`),
    staleTime: 60_000,
    refetchInterval: 2 * 60_000, // online state drives whether Join is enabled
  });
}

/** Technician connections to a session (fetched from ScreenConnect on demand). */
export function useScreenConnectConnections(sessionId: string | null | undefined, enabled = true) {
  return useQuery({
    enabled: enabled && !!sessionId,
    queryKey: ['screenconnect', 'connections', sessionId],
    queryFn: () => api.get<{ items: ScConnection[] }>(`/api/rmm/screenconnect/sessions/${sessionId}/connections`),
    staleTime: 2 * 60_000,
  });
}
